import { Content, CropDetails, SelectedMedia } from '@/lib/model';

import { IMAGE_RATIO } from './constant';
import { Orientations, VideoSubTypes } from './getAssetDimension';
import { isVideoType } from '@/utils/contentType/mediaType';
import { getVideoAspectRatio } from './video/playlistJsonUtils';

type ImageLoaderFormat = 'png' | 'jpg' | 'jpeg' | 'webp';
type ImageLoaderWidth = 48 | 64 | 128 | 256 | 512 | 720 | 1080 | 1440;
type ImageLoaderQuality = 50 | 80 | 100;

/**
 * This function try to find non webp image format for og tag specially for social content sharing
 * @param coverImage
 * @param width
 * @param height
 * @returns
 */
export const getCoverImageForOGImage = (
  coverImage?: string | SelectedMedia | null | undefined,
  width?: number,
  height?: number,
): string | undefined => {
  if ((coverImage as SelectedMedia)?.cropDetails?.length) {
    const croppedImage = getCroppedImage(
      (coverImage as SelectedMedia).cropDetails as Array<CropDetails>,
      width,
      height,
    );
    if (croppedImage?.url)
      return imageUrlLoader(croppedImage.url as string, 'jpg', 720);
  }
  if (coverImage && typeof coverImage === 'string') {
    return coverImage;
  }

  if (typeof (coverImage as SelectedMedia)?.media?.originalUrl === 'string') {
    return imageUrlLoader(
      (coverImage as SelectedMedia)?.media?.originalUrl as string,
      'jpg',
      512,
    );
  }
  if (typeof (coverImage as SelectedMedia)?.media?.thumbnailURL === 'string') {
    return (coverImage as SelectedMedia).media.thumbnailURL as string;
  }

  return undefined;
};

/**
 *
 * @param coverImage
 * @param width
 * @param height
 * @returns
 */
export const getCoverImageUrl = (
  coverImage?: string | SelectedMedia | null | undefined,
  width?: number,
  height?: number,
): string => {
  if ((coverImage as SelectedMedia)?.cropDetails?.length && width && height) {
    const croppedImage = getCroppedImage(
      (coverImage as SelectedMedia).cropDetails as Array<CropDetails>,
      width,
      height,
    );

    if (croppedImage?.optimizedUrl) return croppedImage.optimizedUrl;
    else if (croppedImage?.url) return croppedImage.url;
  }
  if (coverImage) {
    return typeof coverImage === 'string'
      ? coverImage
      : coverImage?.media?.type === 'VIDEO' ||
          coverImage?.media?.type === 'AUDIO'
        ? (coverImage?.media?.imageUrl ??
          coverImage?.media?.thumbnailURL ??
          '/png/placeholder.png')
        : coverImage?.media?.imageUrl ||
          coverImage?.media?.originalUrl ||
          coverImage?.media?.url ||
          '/png/placeholder.png';
  }
  return '/png/placeholder.png';
};

/**
 *
 * This function search through cropped image and pick the one that has nearest aspect ratio to the main image
 * @param cropDetails
 * @param width
 * @param height
 * @returns
 */
export function getCroppedImage(
  cropDetails: Array<CropDetails>,
  width = 0,
  height = 1,
) {
  const mainCrop = cropDetails.find(detail => detail.type === 'MAIN');
  const imageRatio = IMAGE_RATIO.find(
    ({ ratio }) => Math.abs((width / height - ratio) / ratio) < 0.01,
  );

  if (!imageRatio) return mainCrop;
  const matchCrop = cropDetails.find(detail =>
    imageRatio?.type?.includes(detail.type || ''),
  );

  if (!matchCrop && mainCrop) return mainCrop;
  if (matchCrop) {
    return matchCrop;
  }
}

export function imageUrlLoader(
  url: string,
  format: ImageLoaderFormat = 'webp',
  width: ImageLoaderWidth = 1080,
  quality: ImageLoaderQuality = 80,
) {
  const urls = `${url}?width=${width}&format=${format}&quality=${quality}`;
  return urls;
}

export function getImageLoaderWidth(width?: number | string): ImageLoaderWidth {
  if (!width || isNaN(+width)) return 1080;
  const numberW = +width * 1.5;

  const distances = (
    [48, 64, 128, 256, 512, 720, 1080, 1440] as Array<ImageLoaderWidth>
  )
    .map(w => ({ width: w, distance: Math.abs(w - numberW) }))
    .sort((a, b) => a.distance - b.distance);
  // we're selecting highest resolution based on first 2 nearest resolutions and fallback to 1080 pixel if any issue occurs
  return (
    (Math.max(
      distances?.[0]?.width,
      distances?.[1]?.width,
    ) as ImageLoaderWidth) || 1080
  );
}

export function checkIsContentVertical(content?: Content) {
  const isVideo =
    (content?.media as unknown as SelectedMedia)?.media?.type === 'VIDEO';
  let isVertical = false;
  if (isVideo) {
    const ratio = getVideoBoxAspectRatio(
      content?.media as unknown as SelectedMedia,
      true,
    );
    isVertical = Boolean(ratio?.isVertical);
  } else {
    const ratio = getImageBoxAspectRatio(
      content?.media as unknown as SelectedMedia,
      content?.coverImage as unknown as SelectedMedia,
      true,
    );
    isVertical = Boolean(ratio?.isVertical);
  }
  return isVertical;
}

export const videoTypeDirection: Record<VideoSubTypes, Orientations> = {
  dailyVideo: 'vertical',
  videoCard: 'both',
  videoPodcast: 'both',
  tvContent: 'horizontal',
};

export function getImageAspectRatio(
  media: SelectedMedia,
  coverImage?: SelectedMedia,
) {
  if (media?.media?.width && media?.media?.height) {
    return media.media.width / media.media.height;
  } else if (coverImage?.media?.width && coverImage?.media?.height) {
    return coverImage.media.width / coverImage.media.height;
  }
}

export function getImageBoxAspectRatio(
  item: SelectedMedia,
  coverImage?: SelectedMedia,
  calculateAspectRatio?: boolean,
  ignoreAspectRatio?: boolean,
  width?: number,
  height?: number,
) {
  if (calculateAspectRatio) {
    const aspectRatio = getImageAspectRatio(item as SelectedMedia, coverImage);

    if (aspectRatio) {
      return {
        aspectRatio,
        isVertical: aspectRatio < 1,
      };
    }
  }
  if (width && height && !ignoreAspectRatio) {
    const aspectRatio = width / height;
    return {
      aspectRatio,
      isVertical: aspectRatio < 1,
    };
  }
  return {};
}

export function getVideoBoxAspectRatio(
  item: SelectedMedia,
  calculateAspectRatio?: boolean,
  ignoreAspectRatio?: boolean,
  width?: number,
  height?: number,
) {
  const isVideo = isVideoType(item);
  if (calculateAspectRatio) {
    const aspectRatio = isVideo
      ? getVideoAspectRatio(item as SelectedMedia)
      : getImageAspectRatio(item);

    if (aspectRatio) {
      return {
        aspectRatio,
        isVertical: aspectRatio < 1,
      };
    }
  }
  if (width && height && !ignoreAspectRatio) {
    const aspectRatio = width / height;
    return {
      aspectRatio,
      isVertical: aspectRatio < 1,
    };
  }
  return {};
}
